import React, { FC } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Formik, Form } from "formik";
import { object, string } from "yup";
import { AxiosError } from "axios";
import { authAxios as axios } from "shared-auth";
import { useTranslation } from "i18n";
import {
  Button,
  CommonErrorPopup,
  Popup,
  RadioFormik,
  TextAreaFormik,
  ValidationMessage,
} from "component-library";
import { ERROR } from "component-library/src/components/Atoms/InputsCommon/InputWrapper";
import config from "config/global";
import { selfTaperRequestQueryKey } from "pages/SelfTapeRequestPage/constants";
import { dataTestIds } from "data-testids";

export const acceptRequestModalButtonDataTestId =
  dataTestIds.nuJobsApp["SelfTapeRequests.AcceptModal.acceptButton"];

interface Props {
  selfTapeRequestId: string;
  isAccepted?: boolean;
  isRejected?: boolean;
  isAgent?: boolean;
  closeModal: () => void;
  performerReplyComments?: string;
  forwardedToPerformer?: boolean;
}

enum FormValues {
  isAccepted = "isAccepted",
  comments = "comments",
}

interface IFormProps {
  [FormValues.isAccepted]?: number;
  [FormValues.comments]?: string;
}

const AcceptRequestModal: FC<React.PropsWithChildren<Props>> = ({
  selfTapeRequestId,
  isAccepted,
  isRejected,
  isAgent,
  performerReplyComments,
  forwardedToPerformer,
  closeModal,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {
    mutate: submitResponse,
    isLoading,
    error,
  } = useMutation<unknown, AxiosError, IFormProps>({
    mutationFn: ({ isAccepted, comments }: any) => {
      return axios.post(
        `${config.selfTapesApiUrl}/notification/${selfTapeRequestId}/reply`,
        { isAccepted: Boolean(Number(isAccepted)), comments }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [selfTaperRequestQueryKey],
      });
      closeModal();
    },
  });

  const acceptRejectValue = isAccepted ? 1 : 0;
  const isEditResponse = isAccepted || isRejected;
  const showAgentDeclineWarning = isAgent && forwardedToPerformer && isAccepted;

  const initialValues = {
    [FormValues.isAccepted]: isEditResponse ? acceptRejectValue : undefined,
    [FormValues.comments]: performerReplyComments,
  };

  if (error)
    return (
      <CommonErrorPopup
        errorStatusCode={error?.response?.status}
        popupProps={{ close: () => location.reload }}
      />
    );

  return (
    <Formik<IFormProps>
      initialValues={initialValues}
      validationSchema={object({
        [FormValues.isAccepted]: string().required(
          t("common:validation.please.select")
        ),
      })}
      onSubmit={(values) => submitResponse(values)}
    >
      {({ submitForm, errors, dirty }) => (
        <Popup
          width={{ lg: 6, md: 8 }}
          close={closeModal}
          texts={{ closePopup: t("common:button.label.close") }}
          priority="high"
        >
          <div className="g-popup-content">
            <div className="g-popup-content__header">
              {isEditResponse
                ? t("common:selfTapes.request.acceptModal.title.edit")
                : t("common:selfTapes.request.acceptModal.title")}
            </div>
            <div className="g-popup-content__scrollable">
              <Form className="flex flex-col gap-3 px-4 pt-5">
                <RadioFormik
                  name={FormValues.isAccepted}
                  value={1}
                  label={t("common:selfTapes.request.acceptModal.label.accept")}
                />
                <RadioFormik
                  name={FormValues.isAccepted}
                  value={0}
                  label={t(
                    "common:selfTapes.request.acceptModal.label.decline"
                  )}
                />
                <div className="mb-2">
                  {errors[FormValues.isAccepted] && (
                    <ValidationMessage
                      message={errors[FormValues.isAccepted]}
                      type={ERROR}
                    />
                  )}
                </div>
                {showAgentDeclineWarning && (
                  <div className="p-2.5 bg-red-pale border border-red-cardinal rounded">
                    {t("common:selfTapes.request.acceptModal.declineWarning")}
                  </div>
                )}
                <TextAreaFormik
                  name={FormValues.comments}
                  label={t("common:form.label.message.optional")}
                  placeholder={t("common:placeholder.typeTourMessage")}
                  maxLength={200}
                  rows={6}
                  showWordCountInsteadOfMinLength
                />
              </Form>
            </div>
            <hr className="g-split-line" />
            <div className="flex flex-col gap-3 g-popup-content__buttons-set">
              <Button
                type="primary"
                text={t("common:button.label.submitResponse")}
                onClick={submitForm}
                disabled={!dirty || isLoading}
                data-testid={acceptRequestModalButtonDataTestId}
              />
              <Button
                type="secondary"
                text={t("common:button.label.close")}
                onClick={closeModal}
              />
            </div>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default AcceptRequestModal;
